import React, { useEffect, useState } from 'react';
import { Box, GDSStyleObject } from '@leagueplatform/genesis-core';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import {
  useParamsLegacy as useParams,
  useNavigateLegacy as useNavigate,
} from '@leagueplatform/routing';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { LandingComponent } from './landing.component';
import { DetailPageComponent } from './detail-page.component';
import { documentCenter } from '../document-center.config';
import { ErrorModal } from './errorModal';
import {
  getAccessTokensForMultipleDocs,
  scrollToTop,
  getDocumentsEndPoints,
  handleDocumentsConcat,
  sortAndRemoveDuplicate,
  getDocumentsList,
  getPartnerToken,
  getAccessTokenHelper,
  getRecentDocumentListFilerByNinetyDays,
} from '../helper/helpers';
import * as CONSTANTS from '../utils/constant';

export function DocumentCenterComponent() {
  const { id }: any = useParams();
  const documentId = id ? id.toLowerCase() : null;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState(false);
  const [recentDocs, setRecentDocs] = useState<any>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [recentDocsEmpty, setRecentDocsEmpty] = useState<boolean>(false);
  const [allDocs, setAllDocs] = useState<any>({
    eob: [],
    'determination-notices': [],
    'ma-welcome-guides': [],
  });
  const [allEobDocsEmpty, setAllEobDocsEmpty] = useState<boolean>(false);
  const [allumLettersEmpty, setAllumLettersEmpty] = useState<boolean>(false);
  const [tabActive, setTabActive] = useState<string>(`${CONSTANTS.RECENT}`);

  const config = documentCenter.getConfig();
  const {
    HIGHMARK_API_URL,
    HIGHMARK_AXWAY_API_URL,
    enableUMLetters,
    enableWelcomeGuides,
    useCurrentRegionUbkHeader,
  } = config;
  const currentUbk: any = useCurrentRegionUbkHeader() || null;
  const ubkValue = currentUbk?.headers?.Brandubk || null;
  const [groupNumber, setGroupNumber] = useState<string>('');
  const [medicareMember, setMedicareMember] = useState<boolean>(false);
  const [planAPICalled, setPlanAPICalled] = useState<boolean>(false);

  const fireBaseFlagsConfig = {
    um_letters: enableUMLetters,
    welcome_guides: enableWelcomeGuides,
  };
  const navigate = useNavigate();

  const closeErrorModal = () => {
    setErrMsg(false);
  };

  const setDocsEmptyStateTrue = (type: string) => {
    if (type === CONSTANTS.RECENT) {
      setRecentDocsEmpty(true);
    } else if (type === CONSTANTS.EOB) {
      setAllEobDocsEmpty(true);
    } else {
      setAllumLettersEmpty(true);
    }
  };

  const updateDocs = (docType: string, newDoc: any[]) => {
    setAllDocs((prevState: any) => ({
      ...prevState,
      [docType]: newDoc,
    }));
  };

  const fetchDocumentsList = async (document: string) => {
    const endPoints = getDocumentsEndPoints(
      document,
      HIGHMARK_API_URL,
      HIGHMARK_AXWAY_API_URL,
      fireBaseFlagsConfig,
      ubkValue,
      medicareMember,
      groupNumber,
    );

    setIsLoading(true);
    const getDocumentList = await getAccessTokensForMultipleDocs(
      endPoints,
      accessToken,
      ubkValue,
    );

    const response = handleDocumentsConcat(getDocumentList);
    if (response.error) {
      setErrMsg(true);
      setIsLoading(false);
      if (document === CONSTANTS.RECENT) {
        setRecentDocs([]);
      } else {
        updateDocs(document, []);
      }
    } else {
      if (response.list?.length === 0) {
        setDocsEmptyStateTrue(document);
        setRecentDocs([]);
      } else {
        const listData =
          response.list?.length > 1
            ? sortAndRemoveDuplicate(response.list)
            : response.list;
        if (document === CONSTANTS.RECENT) {
          setRecentDocs(
            getRecentDocumentListFilerByNinetyDays(listData).slice(0, 5),
          );
        } else {
          updateDocs(document, listData);
        }
      }
      setIsLoading(false);
    }
  };
  const getIdentityToken = async () => {
    if (!accessToken) {
      setIsLoading(true);
      const response = await getPartnerToken();
      if (response) {
        if (response.token) {
          setAccessToken(response?.token);
          return;
        }
        if (response.errMsg) {
          setIsLoading(false);
          setErrMsg(true);
        }
      }
    }
  };

  useEffect(() => {
    const getMedicareAdvantageMember = async (url: string) => {
      setIsLoading(true);
      getAccessTokenHelper(url, accessToken, '')
        .then((result) => {
          if (!result.errMsg) {
            setGroupNumber(result?.plans?.[0]?.groupNumber);
            const medicareAdvFlag = result?.plans?.[0]?.medicareAdvIndicator;
            setMedicareMember(medicareAdvFlag === 'true');
            setPlanAPICalled(true);
          } else {
            setIsLoading(false);
            setErrMsg(true);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setErrMsg(true);
        });
    };
    if (
      accessToken &&
      accessToken !== undefined &&
      !planAPICalled &&
      fireBaseFlagsConfig.welcome_guides
    ) {
      getMedicareAdvantageMember(
        `${HIGHMARK_API_URL}/digital/v3/members/benefits/plans?planYear=all`,
      );
    }
    const documentTypes = getDocumentsList(
      ubkValue,
      medicareMember,
      fireBaseFlagsConfig,
    );

    const paramsMatch = documentId
      ? Object.keys(documentTypes).includes(documentId)
      : false;
    scrollToTop();
    if (!id) {
      getIdentityToken();
      trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
        screen_name: CONSTANTS.DOCUMENT_CENTER,
        product_area: CONSTANTS.DOCUMENT_CENTER,
        path: window.location.pathname,
        event_author: CONSTANTS.ENGEN,
      });
    } else if (id && !paramsMatch) {
      navigate(CONSTANTS.DOCUMENTS_URL);
    } else {
      getIdentityToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, accessToken, HIGHMARK_API_URL]);

  return (
    <>
      {errMsg && <ErrorModal onClose={closeErrorModal} />}
      {isLoading && (
        <Box
          css={{
            '& >.genesis': {
              zIndex: 2,
              background: 'rgba(255, 255, 255, 0.3)',
              position: 'fixed',
            } as GDSStyleObject,
          }}
        >
          <LoadingIndicator />
        </Box>
      )}
      {!id ? (
        <LandingComponent
          id={documentId}
          ubkValue={ubkValue}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          tabActive={tabActive}
          setTabActive={setTabActive}
          recentDocs={recentDocs}
          recentDocsEmpty={recentDocsEmpty}
          fetchDocumentsList={fetchDocumentsList}
          setErrMsg={setErrMsg}
          accessToken={accessToken}
          medicareMember={medicareMember}
          planAPICalled={planAPICalled}
          fireBaseFlagsConfig={fireBaseFlagsConfig}
        />
      ) : (
        <DetailPageComponent
          id={documentId}
          ubkValue={ubkValue}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          allDocs={allDocs}
          allEobDocsEmpty={allEobDocsEmpty}
          allumLettersEmpty={allumLettersEmpty}
          fetchDocumentsList={fetchDocumentsList}
          setErrMsg={setErrMsg}
          accessToken={accessToken}
          medicareMember={medicareMember}
          fireBaseFlagsConfig={fireBaseFlagsConfig}
        />
      )}
    </>
  );
}
